import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputAdornment, InputLabel, MenuItem, Select, TextField, Grid } from '@mui/material';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import axios from 'axios';
import SampleNameField from 'components/SampleNameField';
import DNASelector from 'components/DNASelector';

export default function PlasmidAssemblyForm({open, row : rows, onClose, setData, onCommit, showMessage, api_update, ...props}) {
  const default_assembly = {method: "gibson", product: {name: "pSPS"}, fragments: {}, volume: 15, incubation_time: 60}
  const [assembly, setAssembly] = useState(default_assembly)
  const [options_dna, setOptionsDna] = useState()
  const [options_plasmids, setOptionsPlasmids] = useState()
  const options_templates = [...(options_dna || []), ...(options_plasmids || [])]
  useEffect(() => {
    axios.get("/api/wetlab/dna/get").then(({data : {data}}) => setOptionsDna(data))
  }, []);
  useEffect(() => {
    axios.get("/api/wetlab/plasmids/get", {params: {include_verified: true}}).then(({data : {data}}) => setOptionsPlasmids(data))
  }, []);
  const handleClose = () => {
    onClose();
    setAssembly(prev => ({...prev, fragments: {}}))
  }
  const handleSubmit = (event) => {
    event.preventDefault()
    axios.post(api_update, {
      ...assembly,
      fragments: Object.fromEntries(Object.values(assembly.fragments).map(([frag, pmol]) => [frag.id, pmol])),
    })
      .then(({status}) => status === 200 && showMessage?.("Plasmid assembly saved"))
      .catch(() => showMessage?.("Error saving plasmid assembly"))
    // onCommit?.(assembly)
    // setData?.(prev => prev.map(x => ({...x, concentration: concentrations[x.id] || x.concentration})))
    handleClose();
  }
  const pmol_from_length = (bp, is_backbone = false) => {
    if (is_backbone)
      return 0.01
    if (!bp)
      return undefined
    return (bp <= 220) ? 0.05 : 0.02
  }
  useEffect(() => {
    if (rows)
    {
      // Assume backbone is longest fragment or first selected
      const backbone_length = Math.max(...rows.map(row => row.length))
      const backbone_index = backbone_length > 0 ? rows.findIndex(row => row.length === backbone_length) : 0
      setAssembly(prev => ({...prev, fragments: {...Object.fromEntries(rows.map((row, i) => [i, [row, pmol_from_length(row.length, i === backbone_index)]])), ...prev.fragments}}))
    }
  }, [rows])
  useEffect(() => {
    // Get recommended plasmid sequence
    if (!assembly.fragments || Object.keys(assembly.fragments).length === 0 || !assembly.method) {
      setAssembly(prev => ({...prev, product: {...prev.product, sequence: ""}}))
      return
    }
    axios.get(`/api/wetlab/protocols/assembly/params?method=${assembly.method}&fragments=${Object.values(assembly.fragments).map(v => v?.[0]?.id).join(",")}`).then(({data : {error, sequence}}) => {
      if (error)
        setAssembly(prev => ({...prev, product: {...prev.product, sequence: ""}}))
      else
        setAssembly(prev => ({...prev, product: {...prev.product, sequence}}))
    })
  }, [assembly.fragments, assembly.method])
  if (!open || !rows)
    return null
  return (
    <Dialog open={open} onClose={handleClose} {...props}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{rows.length === 1 ? `Assemble ${rows[0].name} into Plasmid` : `Assemble ${rows.length} Fragments into Plasmid`}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth variant="standard">
            <InputLabel id="rxntype-label">Method</InputLabel>
            <Select labelId="rxntype-label" label="Method" value={assembly.method} onChange={e => setAssembly(prev => ({...prev, method: e.target.value}))}>
              <MenuItem value={"gibson"}>Gibson</MenuItem>
              <MenuItem value={"ligation_t7"}>T7 Ligation</MenuItem>
              <MenuItem value={"goldengate_esp3i"}>Golden Gate with Esp3I</MenuItem>
              <MenuItem value={"goldengate_bpii"}>Golden Gate with BpiI</MenuItem>
            </Select>
          </FormControl>
          <SampleNameField
            autoFocus
            margin="dense"
            label="Product"
            fullWidth
            variant="standard"
            value={assembly?.product?.name || ""}
            onChange={e => setAssembly(prev => ({...prev, product: {...prev.product, name: e.target.value}}))}
          />
          <Grid container spacing={1}>
            {rows.map((row, fragment_i) =>
              <React.Fragment key={row.id}>
                <Grid size={{ xs: 9 }}>
                  <DNASelector
                    label={`Fragment ${fragment_i + 1}`}
                    options={options_templates}
                    concentrationUnit="ng/μL"
                    onChange={(e, v) => setAssembly(prev => ({...prev, fragments: {...prev.fragments, [fragment_i]: [v, prev.fragments?.[fragment_i]?.[1]]}}))}
                    value={assembly.fragments?.[fragment_i]?.[0] || null}
                  />
                </Grid>
                <Grid size={{ xs: 3 }}>
                  <TextField
                    key={row.id}
                    margin="dense"
                    label={`Amount ${fragment_i + 1}`}
                    fullWidth
                    variant="standard"
                    slotProps={{
                      htmlInput: {inputMode: "decimal"},
                      input: {endAdornment: <InputAdornment position="end">pmol</InputAdornment>},
                    }}
                    sx={{input: {textAlign: "right"}}}            
                    value={assembly.fragments?.[fragment_i]?.[1] || ""}
                    onChange={e => setAssembly(prev => ({...prev, fragments: {...prev.fragments, [fragment_i]: [prev.fragments?.[fragment_i]?.[0], e.target.value]}}))}
                  />
                </Grid>
              </React.Fragment>
            )}
          </Grid>
          <TextField
            margin="dense"
            label="Volume"
            fullWidth
            variant="standard"
            slotProps={{
              htmlInput: {inputMode: "decimal"},
              input: {endAdornment: <InputAdornment position="end">μL</InputAdornment>},
            }}
            sx={{input: {textAlign: "right"}}}            
            value={assembly.volume || ""}
            onChange={e => setAssembly(prev => ({...prev, volume: e.target.value}))}
          />
          <TextField
            margin="dense"
            label="Incubation"
            fullWidth
            variant="standard"
            slotProps={{
              htmlInput: {inputMode: "decimal"},
              input: {endAdornment: <InputAdornment position="end">min</InputAdornment>},
            }}
            sx={{input: {textAlign: "right"}}}            
            value={assembly.incubation_time || ""}
            onChange={e => setAssembly(prev => ({...prev, incubation_time: e.target.value}))}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            variant="standard"
            value={assembly?.product?.description || ""}
            onChange={e => setAssembly(prev => ({...prev, product: {...prev.product, description: e.target.value}}))}
          />
          <TextField
            margin="dense"
            label="Sequence"
            fullWidth
            variant="standard"
            value={assembly?.product?.sequence || ""}
            onChange={e => setAssembly(prev => ({...prev, product: {...prev.product, sequence: e.target.value}}))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit">Done</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export const PLASMIDASSEMBLY_FORM_MENUACTION_DEF = (api_update) => ({
  label: "Assemble into Plasmid",
  icon: <DataUsageIcon />,
  Form: (props) => <PlasmidAssemblyForm api_update={api_update} {...props} />,
  multiple: true,
})
